import { useEffect, useState, useCallback } from "react";
import logger from "../../utils/logger";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { Trophy, Users, Rocket, Medal, CurrencyInr, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatINR(num) {
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  return `₹${Number(num).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const tabs = [
  { id: "earners", label: "Top Earners", icon: Trophy, color: "#F59E0B" },
  { id: "referrals", label: "Direct Champions", icon: Users, color: "#3b82f6" },
  { id: "growing", label: "Fastest Growing", icon: Rocket, color: "#10B981" },
];

const rankColors = ["#F59E0B", "#A1A1AA", "#CD7F32"];

function RankBadge({ rank }) {
  if (rank <= 3) {
    return (
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
        style={{ background: `${rankColors[rank - 1]}20`, color: rankColors[rank - 1], border: `1.5px solid ${rankColors[rank - 1]}40` }}
      >
        {rank}
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-zinc-800/50 text-zinc-500">
      {rank}
    </div>
  );
}

export default function Leaderboard() {
  const [activeTab, setActiveTab] = useState("earners");
  const [data, setData] = useState({ entries: [], title: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${AFF_API}/affiliate/leaderboard?tab=${tab}`, { headers: getAffHeaders() });
      setData(res.data);
    } catch (err) {
      logger.error("Leaderboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(activeTab); }, [activeTab, fetchData]);

  const isEarners = activeTab === "earners";

  return (
    <div className="space-y-5" data-testid="leaderboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Trophy size={22} weight="duotone" className="text-amber-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: "Manrope" }}>Leaderboard</h2>
            <p className="text-xs text-zinc-500">See how you compare with other affiliates</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 rounded-xl bg-zinc-900/50 border border-zinc-800/50" data-testid="leaderboard-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`leaderboard-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition-all duration-300 ${
                isActive
                  ? "bg-zinc-800 text-zinc-50 shadow-lg"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/40"
              }`}
            >
              <Icon size={14} weight={isActive ? "duotone" : "regular"} style={isActive ? { color: tab.color } : {}} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Leaderboard List */}
      <div className="rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md overflow-hidden" data-testid="leaderboard-list">
        {loading ? (
          <div className="h-60 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.entries.length === 0 ? (
          <div className="py-16 text-center">
            <Trophy size={32} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No leaderboard data yet</p>
            <p className="text-xs text-zinc-600 mt-1">Click "Load Demo" to populate</p>
          </div>
        ) : (
          <>
            {/* Top 3 podium */}
            {data.entries.length >= 3 && (
              <div className="grid grid-cols-3 gap-3 p-5 border-b border-zinc-800/50" data-testid="leaderboard-podium">
                {[1, 0, 2].map((idx) => {
                  const entry = data.entries[idx];
                  if (!entry) return null;
                  const isFirst = idx === 0;
                  return (
                    <div
                      key={entry.rank}
                      className={`flex flex-col items-center text-center ${isFirst ? "order-1 -mt-2" : idx === 1 ? "order-0 mt-4" : "order-2 mt-4"}`}
                    >
                      <div className="relative mb-2">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isFirst ? "bg-amber-500/20 ring-2 ring-amber-500/40" : "bg-zinc-800"}`}>
                          <span className="text-lg font-bold" style={{ color: rankColors[entry.rank - 1] }}>{entry.rank}</span>
                        </div>
                        {isFirst && (
                          <Medal size={16} weight="fill" className="text-amber-500 absolute -top-1 -right-1" />
                        )}
                      </div>
                      <p className="text-xs font-medium text-zinc-200 truncate max-w-full">{entry.name}</p>
                      <p className="text-sm font-bold mt-0.5" style={{ color: rankColors[entry.rank - 1], fontFamily: "JetBrains Mono, monospace" }}>
                        {isEarners ? formatINR(entry.value) : entry.value}
                      </p>
                      <p className="text-[10px] text-zinc-600">{isEarners ? "Total" : entry.label}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rest of list */}
            <div className="divide-y divide-zinc-800/30">
              {data.entries.slice(3).map((entry) => (
                <div
                  key={entry.rank}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-zinc-800/20 transition-colors"
                >
                  <RankBadge rank={entry.rank} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-200 truncate">{entry.name}</p>
                    <p className="text-[10px] text-zinc-600">
                      {isEarners ? `${entry.referrals || 0} referrals` : `${formatINR(entry.earnings || 0)} earned`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-zinc-100" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                      {isEarners ? formatINR(entry.value) : entry.value}
                    </p>
                    <p className="text-[10px] text-zinc-600">{entry.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
